import { useDispatch, useSelector } from 'react-redux';
import { getDefaultSubscriptionSelector } from '@modules/subscriptions/core/selectors';
import { resumeSubscriptionRequest } from '@modules/subscriptions/core/actions';
import { Headline, Modal } from '@components/index';
import { SubscriptionReactivationInfo } from '../SubscriptionReactivationInfo';
import { getSubscriptionResume } from '@constants/subscription';
import { KTSVG, toAbsoluteUrl, useClickOutside } from '@shared';
import { useRef, useState } from 'react';
import { calcDeliveryDate } from '@modules/subscriptions/utils/calcDeliveryDate';
import { SubscriptionReactivationModal } from '../reactivation-modal/SubscriptionReactivationModal';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

export const SubscriptionResume = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const today = new Date().toISOString();

  useClickOutside(ref, () => {
    setIsModalOpen(false);
  });

  const [dateFrom, dateTo] = calcDeliveryDate(today);
  const handleClose = () => setIsModalOpen(false);

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleResumeSubscription = () => {
    dispatch(
      resumeSubscriptionRequest({
        id: defaultSubscription?.id!,
      }),
    );
  };

  return (
    <>
      <div className="flex justify-between mx-[1.5rem] md:hidden mb-[1rem] items-center">
        <span className="subscription-reactivation-status h-fit subscription-reactivation-status-paused ">
          <FormattedMessage id="SUBSCRIPTION.STATUS.PAUSED" />
        </span>
        <Headline className="!mb-0" hasDropdown></Headline>
      </div>

      <Headline className="!mb-[2rem] md:!mb-[2rem] mx-[1.5rem]">
        <FormattedMessage id="SUBSCRIPTION.REACTIVATE.PAGE_HEADING" />
      </Headline>

      <div className="md:flex gap-[1.2rem]">
        <SubscriptionReactivationInfo
          {...getSubscriptionResume(intl)}
          onReactivateClick={handleResumeSubscription}
        />

        <div className="paused-subscription">
          <h3>
            <FormattedMessage
              id="SUBSCRIPTION.PAUSED_HEADING"
              values={{ strong: (chunks) => <strong>{chunks}</strong> }}
            />
          </h3>
          <picture>
            <source
              media="(min-width: 768px)"
              srcSet={toAbsoluteUrl(
                '/media/svg/subscription-reactivation/paused-subscription-desktop.webp',
              )}
            />
            <img
              src={toAbsoluteUrl(
                '/media/svg/subscription-reactivation/paused-subscription-mobile.webp',
              )}
              alt="subscription-reactivation"
            />
          </picture>
          <div className="subscription-reactivation-action">
            <button
              className="flex hover:text-[#2A9890] font-bold transition duration-200 items-center gap-[8px]"
              onClick={handleModalToggle}>
              <KTSVG path={toAbsoluteUrl('/media/svg/general/edit.svg')} />
              <FormattedMessage id="SUBSCRIPTION.SHIPPING_AND_PAYMENT" />
            </button>
            {isModalOpen && (
              <Modal isOpen={true} handleClose={handleClose}>
                <SubscriptionReactivationModal />
              </Modal>
            )}
          </div>
          <div className="subscription-reactivation-calendar-delivery">
            <span>
              <FormattedMessage id="SUBSCRIPTION.DELIVERY_DATE" />
            </span>
            <FormattedDate
              value={
                new Date(
                  new Date(defaultSubscription?.nextBillingDate!).setDate(
                    new Date(defaultSubscription?.nextBillingDate!).getDate() - 2,
                  ),
                )
              }
              day="2-digit"
            />
            {' - '}
            <FormattedDate
              value={new Date(defaultSubscription?.nextBillingDate!)}
              year="numeric"
              month="long"
              day="2-digit"
            />
          </div>
        </div>
      </div>
    </>
  );
};
